<template>
  <Teleport to="#header-left" defer>
    <span class="heading-serif-sm font-normal text-secondary-black">
      {{ t("impersonate_users.title") }}
    </span>
  </Teleport>

  <div class="flex flex-col gap-8 pb-4">
    <p class="text-subtitle text-medium-gray">
      {{ t("impersonate_users.subtitle") }}
    </p>

    <IconField icon-position="left" class="w-full max-w-md">
      <InputIcon>
        <IconMaterialSymbolsSearch class="m-auto" />
      </InputIcon>
      <InputText
        v-model="search"
        :placeholder="t('impersonate_users.search_placeholder')"
        class="w-full rounded-pill bg-white"
      />
    </IconField>

    <section class="flex w-full flex-col gap-4">
      <h2 class="text-lg font-medium tracking-admin text-near-black">
        {{ t("impersonate_users.all_users") }}
      </h2>
      <p v-if="authStore.isImpersonating" class="text-base text-medium-gray">
        {{ t("impersonate_users.already_impersonating") }}
      </p>
      <UserTable
        v-else-if="!missingPermission"
        class="bg-white"
        :max-rows="15"
        :search="true"
        :multiselect="true"
        :users="filteredUsers"
        :internal="true"
        @impersonate="impersonateUser($event)"
      />
      <p v-else class="text-base text-medium-gray">
        {{ t("impersonate_users.missing_permission") }}
      </p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { useSeoMeta } from "@unhead/vue";
import { IconField, InputIcon, InputText } from "primevue";
import type { EmployeesResponse } from "shared-core/types/user.ts";
import { buildUserFullName } from "shared-core/utils/all-utils.ts";
import UserTable from "shared-ui/components/UserTable.vue";
import type { EmployeeDataMainStore } from "shared-ui/types/main-store.ts";
import { useApi } from "shared-ui/utils/api";
import { TASKS_ROUTE_NAME } from "shared-ui/utils/route-names";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { router } from "@/router.ts";
import { useAuthStore } from "@/stores/auth-store.ts";

const { t } = useI18n();

useSeoMeta({
  title: () => t("impersonate_users.title"),
});

definePage({
  meta: {
    requiresAuth: true,
    internal: true,
    layout: "AppLayout",
  },
});

const api = useApi();
const authStore = useAuthStore();
const users = ref<EmployeeDataMainStore[]>([]);
const missingPermission = ref<boolean>(false);

async function loadUsers(): Promise<void> {
  try {
    const response = await api.get<EmployeesResponse>("impersonate/list-users");
    users.value = (await response.data).employees.map((user) => ({
      ...user,
      fullName: buildUserFullName(user),
    }));
  } catch (error) {
    console.error("Failed to list users", error);

    missingPermission.value = true;
  }
}

// Logging out of an impersonation pushes this same route, so the page stays mounted.
watch(
  () => authStore.isImpersonating,
  async (isImpersonating) => {
    if (!isImpersonating) {
      await loadUsers();
    }
  },
  { immediate: true },
);

const search = ref<string | null>(null);

const filteredUsers = computed(() =>
  users.value.filter(
    (u) =>
      search.value === null ||
      u.fullName.includes(search.value) ||
      u.email.includes(search.value),
  ),
);

async function impersonateUser(id: number): Promise<void> {
  const response = await api.post("auth/impersonate", {
    json: {
      userIdToImpersonate: id,
    },
  });

  // If impersonate succeeded, refresh auth store and navigate to tasks
  if (response.responseStatus === 204) {
    await Promise.all([
      authStore.checkAuth(true),
      authStore.fetchOrganization(true),
    ]);
    await router.push({ name: TASKS_ROUTE_NAME });

    return;
  }
}
</script>
